import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWalletRounded,
  AddRounded,
  LockRounded,
  PointOfSaleRounded,
  RemoveRounded,
  StorefrontRounded,
} from '@mui/icons-material';
import api from '../api/api';
import { readAuthSession } from '../auth/authStorage';

const DENOMINATIONS = [
  { value: 20000, label: '20.000', img: '/assets/b20000.jpg', type: 'bill' },
  { value: 10000, label: '10.000', img: '/assets/b10000.jpg', type: 'bill' },
  { value: 5000, label: '5.000', img: '/assets/b5000.jpg', type: 'bill' },
  { value: 2000, label: '2.000', img: '/assets/b2000.jpg', type: 'bill' },
  { value: 1000, label: '1.000', img: '/assets/b1000.jpg', type: 'bill' },
  { value: 500, label: '500', img: '/assets/m500.jpg', type: 'coin' },
  { value: 100, label: '100', img: '/assets/m100.jpg', type: 'coin' },
  { value: 50, label: '50', img: '/assets/m50.jpg', type: 'coin' },
  { value: 10, label: '10', img: '/assets/m10.jpg', type: 'coin' },
];

const BILLS = DENOMINATIONS.filter((item) => item.type === 'bill');
const COINS = DENOMINATIONS.filter((item) => item.type === 'coin');

const money = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

function numberValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function parseCLP(value) {
  const raw = String(value ?? '').replace(/[^0-9-]/g, '');
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCLPInput(value) {
  const raw = String(value ?? '').replace(/\D/g, '');
  if (!raw) return '';
  return new Intl.NumberFormat('es-CL').format(Number(raw));
}

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function normalizeCajaResponse(data) {
  if (!data) return null;
  if (data?.caja && typeof data.caja === 'object') return data.caja;
  if (data?.data?.caja && typeof data.data.caja === 'object') return data.data.caja;
  if (data?.data && typeof data.data === 'object') return data.data;
  return data;
}

function getCajaId(caja) {
  return caja?.id ?? caja?.caja_id ?? caja?.id_caja ?? null;
}

function permissionsFromSession() {
  const tenant = readAuthSession()?.tenant || {};
  const source = Array.isArray(tenant?.permissions)
    ? tenant.permissions
    : Array.isArray(tenant?.permisos)
      ? tenant.permisos
      : [];
  return source.map((value) => String(value?.name || value?.permission || value || '').trim().toLowerCase());
}

function requiereMontoCierre(medio = {}) {
  const tipo = normalizeKey(medio?.tipo || 'OTRO');
  if (medio?.usa_terminal === true) return true;
  return medio?.requiere_liquidacion === true && ['TARJETA', 'PASARELA'].includes(tipo);
}

async function fetchCajaCompleta() {
  const { data } = await api.get('/pos_cajas/estado', { timeout: 7000 });
  const abierta = data?.abierta === true || data?.isAbierta === true || data?.caja_abierta === true;
  if (!abierta) return { abierta: false, caja: null };

  const base = normalizeCajaResponse(data);
  const id = getCajaId(base);
  if (!id) return { abierta: true, caja: base };

  try {
    const { data: detalle } = await api.get(`/pos_cajas/${id}/estado`, { timeout: 7000 });
    return {
      abierta: true,
      caja: { ...(base || {}), ...(normalizeCajaResponse(detalle) || {}) },
    };
  } catch (_) {
    return { abierta: true, caja: base };
  }
}

function DenominationCard({ item, value, onChange, disabled }) {
  const count = Math.max(0, parseInt(value, 10) || 0);
  const subtotal = count * item.value;

  const setCount = (next) => onChange?.(Math.max(0, Number(next) || 0));

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        p: 0.8,
        borderRadius: 2.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: count > 0 ? 'action.hover' : 'background.paper',
        transition: 'background-color .15s ease, border-color .15s ease',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" fontWeight={800}>
          ${item.label}
        </Typography>
        <Typography variant="caption" fontWeight={950} color={subtotal > 0 ? 'primary.main' : 'text.secondary'}>
          {money.format(subtotal)}
        </Typography>
      </Stack>

      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        title={`Agregar ${money.format(item.value)}`}
        onClick={() => !disabled && setCount(count + 1)}
        onKeyDown={(event) => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setCount(count + 1);
          }
        }}
        sx={{
          minHeight: item.type === 'bill' ? 52 : 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          bgcolor: '#f8fafc',
          cursor: disabled ? 'default' : 'pointer',
          overflow: 'hidden',
          px: 0.5,
          '&:hover': disabled ? undefined : { bgcolor: '#f1f5f9' },
        }}
      >
        <Box
          component="img"
          src={item.img}
          alt={`$${item.label}`}
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: item.type === 'bill' ? 46 : 40,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        <Tooltip title="Restar 1">
          <span>
            <IconButton
              size="small"
              onClick={() => setCount(count - 1)}
              disabled={disabled || count <= 0}
            >
              <RemoveRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          size="small"
          value={value ?? ''}
          onChange={(event) => onChange?.(event.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center' } }}
          placeholder="0"
          disabled={disabled}
          sx={{
            width: 62,
            '& .MuiInputBase-root': { height: 36 },
          }}
        />

        <Tooltip title="Sumar 1">
          <span>
            <IconButton size="small" onClick={() => setCount(count + 1)} disabled={disabled}>
              <AddRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

export default function CajaModal({ open, onClose, onUpdated, onNotify, bloquearCierre = false }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estado, setEstado] = useState({ abierta: false, caja: null });
  const [montoInicial, setMontoInicial] = useState('');
  const [counts, setCounts] = useState({});
  const [confirmaciones, setConfirmaciones] = useState({});
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const permissions = useMemo(() => permissionsFromSession(), [open]);
  const canOpen = permissions.includes('caja:sesion:abrir');
  const canClose = permissions.includes('caja:sesion:cerrar');
  const caja = estado?.caja || {};
  const cajaId = getCajaId(caja);

  const mediosCierre = useMemo(() => {
    const medios = Array.isArray(caja?.medios_pago) ? caja.medios_pago : [];
    const list = medios
      .map((medio) => ({
        ...medio,
        clave: normalizeKey(medio?.clave),
        nombre: String(medio?.nombre || medio?.clave || 'Medio de pago'),
        total: numberValue(medio?.total),
      }))
      .filter((medio) => medio.clave && medio.total > 0 && requiereMontoCierre(medio));

    const transbank = numberValue(caja?.ventas_transbank ?? caja?.saldo_teorico_transbank);
    if (transbank > 0 && !list.some((medio) => medio.clave === 'TRANSBANK')) {
      list.push({
        clave: 'TRANSBANK',
        nombre: 'Transbank',
        total: transbank,
        tipo: 'TARJETA',
        usa_terminal: true,
        requiere_liquidacion: true,
      });
    }

    return list;
  }, [caja]);

  const totalArqueo = useMemo(
    () => DENOMINATIONS.reduce(
      (sum, item) => sum + item.value * Math.max(0, parseInt(counts?.[item.value], 10) || 0),
      0
    ),
    [counts]
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    setCounts({});
    setConfirmaciones({});
    setPin('');
    setPinOpen(false);

    try {
      const next = await fetchCajaCompleta();
      setEstado(next);
      if (!next.abierta) setMontoInicial('');
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'No fue posible cargar la caja.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const notify = useCallback((message, severity = 'info') => {
    onNotify?.(message, severity);
  }, [onNotify]);

  const abrirCaja = async () => {
    if (!canOpen) {
      setError('El perfil no tiene permiso caja:sesion:abrir.');
      return;
    }
    if (montoInicial === '') {
      setError('Ingrese el monto inicial de caja.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/pos_cajas/abrir', {
        monto_inicial: parseCLP(montoInicial),
      });
      const cajaAbierta = normalizeCajaResponse(data);
      notify('Caja abierta correctamente.', 'success');
      window.dispatchEvent(new Event('cajaActualizada'));
      await onUpdated?.({ abierta: true, caja: cajaAbierta });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'No fue posible abrir la caja.');
    } finally {
      setSubmitting(false);
    }
  };

  const validarCierre = () => {
    if (!canClose) return 'El perfil no tiene permiso caja:sesion:cerrar.';
    if (bloquearCierre) return 'Hay una venta en curso. Finalice o quite los productos del carrito antes de cerrar la caja.';
    if (!cajaId) return 'No fue posible identificar la caja abierta.';

    const medioPendiente = mediosCierre.find(
      (medio) => String(confirmaciones?.[medio.clave]?.monto || '').trim() === ''
    );
    if (medioPendiente) return `Ingrese el total informado por ${medioPendiente.nombre}.`;

    return '';
  };

  const pedirPin = () => {
    const validation = validarCierre();
    if (validation) {
      setError(validation);
      return;
    }
    setError('');
    setPin('');
    setPinOpen(true);
  };

  const cerrarCaja = async () => {
    const validation = validarCierre();
    if (validation) {
      setError(validation);
      return;
    }

    const pinSanitized = String(pin || '').trim();
    if (!/^[a-zA-Z0-9]{4,}$/.test(pinSanitized)) {
      setError('El PIN debe tener al menos 4 caracteres alfanuméricos.');
      return;
    }

    const tenantid = String(readAuthSession()?.tenantid || '').trim();
    if (!tenantid) {
      setError('No se encontró una empresa activa para validar el PIN.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const pinResponse = await api.post(
        '/auth/pin-login',
        { pin: pinSanitized, tenantid },
        { headers: { tenantid }, timeout: 5000 }
      );
      const pinToken = pinResponse?.data?.token;
      if (!pinToken) throw new Error('La validación del PIN no entregó una autorización válida.');

      const body = {
        monto_efectivo_real: totalArqueo,
        detalle_arqueo: Object.fromEntries(
          DENOMINATIONS.map((item) => [
            String(item.value),
            Math.max(0, parseInt(counts?.[item.value], 10) || 0),
          ])
        ),
      };

      if (mediosCierre.length) {
        body.confirmacion_medios = Object.fromEntries(
          mediosCierre.map((medio) => [
            medio.clave,
            {
              monto_real: parseCLP(confirmaciones?.[medio.clave]?.monto || '0'),
              referencia: String(confirmaciones?.[medio.clave]?.referencia || '').trim() || null,
            },
          ])
        );
      }

      const { data } = await api.post(
        `/pos_cajas/${cajaId}/cerrar`,
        body,
        { headers: { 'X-PIN-Token': pinToken } }
      );

      const diferencia = numberValue(data?.caja?.diferencia_efectivo ?? data?.diferencia_efectivo);
      const suffix = diferencia === 0
        ? ' Cuadre de efectivo: OK.'
        : ` Diferencia de efectivo: ${money.format(diferencia)}.`;

      notify(
        `${data?.mensaje || `Caja #${cajaId} cerrada correctamente.`}${suffix}`,
        diferencia === 0 ? 'success' : 'warning'
      );
      setPinOpen(false);
      window.dispatchEvent(new Event('cajaActualizada'));
      await onUpdated?.({ abierta: false, caja: null });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'No fue posible cerrar la caja.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDenominations = (items) => (
    <Grid container spacing={0.75}>
      {items.map((item) => (
        <Grid item xs={6} sm={item.type === 'bill' ? 4 : 3} md={item.type === 'bill' ? 2.4 : 3} key={item.value}>
          <DenominationCard
            item={item}
            value={counts?.[item.value] ?? ''}
            onChange={(nextValue) => setCounts((current) => ({
              ...current,
              [item.value]: String(nextValue),
            }))}
            disabled={submitting || !canClose || bloquearCierre}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth={estado.abierta ? 'lg' : 'xs'}>
        <DialogTitle sx={{ py: 1.15, px: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
              <StorefrontRounded color={estado.abierta ? 'primary' : 'action'} />
              <Box minWidth={0}>
                <Typography variant="h6" fontWeight={950} lineHeight={1.15}>
                  {estado.abierta ? `Caja #${cajaId || ''}` : 'Abrir caja'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {estado.abierta ? 'Arqueo y cierre de turno' : 'Ingrese el fondo inicial para comenzar a vender'}
                </Typography>
              </Box>
            </Stack>

            {estado.abierta && !loading && (
              <Paper
                variant="outlined"
                sx={{
                  flexShrink: 0,
                  px: { xs: 1.25, sm: 1.8 },
                  py: 0.65,
                  borderRadius: 2.5,
                  bgcolor: 'primary.50',
                  borderColor: 'primary.200',
                  textAlign: 'right',
                  minWidth: { xs: 150, sm: 205 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={800}
                  sx={{ display: 'block', lineHeight: 1 }}
                >
                  Total contado
                </Typography>
                <Typography
                  variant="h5"
                  color="primary.main"
                  fontWeight={950}
                  sx={{ mt: 0.25, lineHeight: 1.1 }}
                >
                  {money.format(totalArqueo)}
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: '14px !important', px: { xs: 1.25, sm: 2 }, pb: 1.25 }}>
          {loading ? (
            <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
              <Stack alignItems="center" spacing={1.2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Consultando estado de caja…</Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

              {!estado.abierta ? (
                <>
                  {!canOpen && <Alert severity="warning">El perfil no tiene permiso para abrir caja.</Alert>}
                  <TextField
                    autoFocus
                    fullWidth
                    label="Monto inicial"
                    value={montoInicial}
                    onChange={(event) => setMontoInicial(formatCLPInput(event.target.value))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !submitting) abrirCaja();
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    InputProps={{ startAdornment: <AccountBalanceWalletRounded sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    disabled={submitting || !canOpen}
                  />
                </>
              ) : (
                <>
                  {!canClose && <Alert severity="warning">El perfil no tiene permiso para cerrar caja.</Alert>}
                  {bloquearCierre && (
                    <Alert severity="warning">
                      Hay una venta en curso. Finalice o quite los productos del carrito antes de cerrar la caja.
                    </Alert>
                  )}

                  <Paper
                    variant="outlined"
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 2.25,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 0.2, sm: 1 }}
                      alignItems={{ sm: 'center' }}
                    >
                      <Typography variant="subtitle2" fontWeight={950} sx={{ whiteSpace: 'nowrap' }}>
                        Cierre ciego · efectivo contado
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pulse un billete o moneda para sumar una unidad, o escriba la cantidad.
                      </Typography>
                    </Stack>
                  </Paper>

                  <Stack spacing={0.6}>
                    <Typography variant="subtitle2" fontWeight={950}>Billetes</Typography>
                    {renderDenominations(BILLS)}
                  </Stack>

                  <Stack spacing={0.6}>
                    <Typography variant="subtitle2" fontWeight={950}>Monedas</Typography>
                    {renderDenominations(COINS)}
                  </Stack>

                  {mediosCierre.length > 0 && (
                    <Stack spacing={0.8}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={950}>Comprobantes / medios electrónicos</Typography>
                        <Typography variant="body2" color="text.secondary">Ingrese el total informado por cada terminal o pasarela.</Typography>
                      </Box>
                      {mediosCierre.map((medio) => (
                        <Paper key={medio.clave} variant="outlined" sx={{ p: 1, borderRadius: 2.25 }}>
                          <Grid container spacing={0.8} alignItems="center">
                            <Grid item xs={12} md={4}>
                              <Typography fontWeight={900}>{medio.nombre}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Total según voucher"
                                value={confirmaciones?.[medio.clave]?.monto || ''}
                                onChange={(event) => setConfirmaciones((current) => ({
                                  ...current,
                                  [medio.clave]: {
                                    ...(current?.[medio.clave] || {}),
                                    monto: formatCLPInput(event.target.value),
                                  },
                                }))}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                disabled={submitting || !canClose || bloquearCierre}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="N° voucher / lote"
                                value={confirmaciones?.[medio.clave]?.referencia || ''}
                                onChange={(event) => setConfirmaciones((current) => ({
                                  ...current,
                                  [medio.clave]: {
                                    ...(current?.[medio.clave] || {}),
                                    referencia: event.target.value,
                                  },
                                }))}
                                disabled={submitting || !canClose || bloquearCierre}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 1.05, minHeight: 56 }}>
          <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
          {!loading && !estado.abierta && (
            <Button
              variant="contained"
              color="success"
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PointOfSaleRounded />}
              onClick={abrirCaja}
              disabled={submitting || !canOpen || montoInicial === ''}
            >
              Abrir caja
            </Button>
          )}
          {!loading && estado.abierta && (
            <Button
              variant="contained"
              color="error"
              startIcon={<LockRounded />}
              onClick={pedirPin}
              disabled={submitting || !canClose || bloquearCierre}
            >
              Cerrar caja
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={pinOpen} onClose={submitting ? undefined : () => setPinOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Typography variant="h6" fontWeight={950}>Confirmar cierre de caja</Typography>
          <Typography variant="body2" color="text.secondary">Ingrese un PIN autorizado para cerrar la caja.</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={1.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="PIN"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !submitting) {
                  event.preventDefault();
                  cerrarCaja();
                }
              }}
              inputProps={{ autoComplete: 'off' }}
              disabled={submitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPinOpen(false)} disabled={submitting}>Volver</Button>
          <Button
            variant="contained"
            color="error"
            onClick={cerrarCaja}
            disabled={submitting || String(pin || '').trim().length < 4}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Confirmar cierre'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
